import { CircularProgress } from '@mui/material'
import { getSignatureImageCardComponent, updateImage } from 'api'
import { BoxDocuments, BoxSwitch, Button, handleValid, Input } from 'components'
import { useToast } from 'hooks'
import {
	DocumentItem,
	DocumentsInputs,
	Icons,
	TabMyOrganizationItem,
	UpdateImageResponse
} from 'interfaces'
import { dontEmojis } from 'lib'
import dayjs from 'lib/dayjs'
import React, { useEffect, useState } from 'react'
import {
	Controller,
	Path,
	SubmitHandler,
	useFieldArray,
	useForm
} from 'react-hook-form'
import Cookies from 'universal-cookie'
import { configGenaral, getExtFromBase64, handleUploadPDF } from 'utils'
import { PropsComponentsMyOrganization } from './interfaceSlide.type'

export const ComponentDocuments: React.FC<PropsComponentsMyOrganization> = ({
	pos,
	name,
	setValue: setValueForm,
	setErrorForm,
	watch: watchParent,
	setFieldError
}): JSX.Element => {
	const data = watchParent(
		`${name}.${pos}.component_value` as Path<{ tabs: TabMyOrganizationItem[] }>
	) as {
		documents?: Array<DocumentItem>
		title_section: string
	}
	const { showToast } = useToast()
	const [documentUpload, setDocumentUpload] = useState<DocumentsInputs>()
	const [loading, setLoading] = useState(false)
	const cookies = new Cookies()
	const organizationId = cookies.get('organization_id')

	const {
		control,
		setValue,
		watch,
		register,
		handleSubmit,
		formState: { errors, isValid }
	} = useForm<{
		title: string
		url: string
		is_private: boolean
		size: number
		documents: DocumentItem[]
		title_section: string
	}>({
		mode: 'onChange',
		defaultValues: {
			title: '',
			url: '',
			is_private: false,
			size: 0,
			documents: data.documents ?? [],
			title_section: data.title_section
		}
	})
	const title = watch('title')
	const titleSection = watch('title_section')

	const { fields, remove, append } = useFieldArray({
		control,
		name: 'documents'
	})

	const nameType = `documents-${name}-${pos}`

	useEffect(() => {
		const isValidForm =
			!watch().title_section.trim() || watch().documents.length === 0
		handleValid(nameType, !isValidForm, setFieldError)
		setErrorForm?.(isValidForm)
	}, [watch().documents, watch().title_section, nameType])

	useEffect(() => {
		setValueForm(
			`${name}.${pos}.component_value.documents` as Path<{
				tabs: TabMyOrganizationItem[]
			}>,
			watch().documents
		)
		setValueForm(
			`${name}.${pos}.component_value.title_section` as Path<{
				tabs: TabMyOrganizationItem[]
			}>,
			watch().title_section
		)
	}, [watch().documents, watch().title_section])

	const updateDocuments = async (
		credentials: UpdateImageResponse | null,
		file: string,
		nameFile: string
	): Promise<string> => {
		if (!credentials) return ''
		const getFile = await fetch(file)
		const fileBlob = await getFile.blob()
		const newName = nameFile.replaceAll('#', 'nro')
		// eslint-disable-next-line no-template-curly-in-string
		const newKey = credentials.fields.key.replace('${filename}', newName)
		const body = {
			...credentials.fields,
			file: fileBlob,
			key: newKey
		}
		await updateImage(credentials.url, body)
		const url = `${credentials.url}${newKey}`
		return url
	}

	const onSubmit: SubmitHandler<{
		title: string
		url: string
		is_private: boolean
		size: number
		documents: DocumentItem[]
		title_section: string
	}> = async values => {
		setLoading(true)
		const credentialsDocuments = await getSignatureImageCardComponent(
			organizationId,
			'/documents'
		)

		if (documentUpload && documentUpload.pdf) {
			const type = getExtFromBase64(documentUpload?.pdf)
			const fileExtension = type ? `.${type}` : ''

			const url = await updateDocuments(
				credentialsDocuments,
				documentUpload.pdf,
				`${values.title.replaceAll(' ', '_')}-${dayjs().format(
					'YYYYMMDDHHmmss'
				)}${fileExtension}`
			)
			append({
				is_private: values.is_private,
				title: values.title,
				url,
				size: documentUpload?.size ?? 0
			})
			setValue('title', '')
			setDocumentUpload(undefined)
			setLoading(false)
		}
	}

	const rules = {
		title: {
			required: { value: true, message: 'This field is required' },
			pattern: {
				value: dontEmojis,
				message: 'Invalid Field'
			}
		},
		title_section: {
			required: { value: true, message: 'This field is required' }
		}
	}

	const stateChange = (): JSX.Element => {
		switch (documentUpload) {
			case undefined:
				return (
					<Button
						type="button"
						label="Upload"
						color="Gray3"
						className="w-full mt-4"
						iconLeft={Icons.upload}
						onClick={() => {
							const input = document.getElementById(`pdf-${name}-${pos}`)
							if (input) input.click()
						}}
						disabled={!isValid}
					/>
				)

			default:
				return (
					<BoxDocuments
						size={documentUpload.size}
						title={documentUpload.name_file}
						onClick={() => setDocumentUpload(undefined)}
						labelButton="Delete PDF"
						classNameButton="!max-w-[162px] !min-w-[112px]"
					/>
				)
		}
	}

	return (
		<div className="flex flex-col justify-between overflow-hidden p-2">
			<div className="flex flex-col w-full overflow-y-auto max-h-[calc(100vh-130px)] pb-6">
				<Controller
					control={control}
					name="title_section"
					render={() => {
						return (
							<Input
								name="title_section"
								title="Section title"
								type="text"
								register={register}
								rules={rules.title_section}
								error={errors.title_section}
								watchValue={titleSection}
								setValueInput={setValue}
								borderFull={false}
								inputClassName="!p-0 rounded"
								titleClassName="!text-[12px] !leading-[12px]"
							/>
						)
					}}
				/>

				<form onSubmit={handleSubmit(onSubmit)} className="mt-5">
					<div>
						<Input
							name="title"
							title="Title"
							register={register}
							rules={rules.title}
							placeholder="Title"
							error={errors.title}
							className="mb-4"
							limit
							cantLimit={configGenaral.maxTitleLength}
							watchValue={title}
							setValueInput={setValue}
						/>

						<input
							id={`pdf-${name}-${pos}`}
							type="file"
							className="hidden"
							onChange={event => {
								if (event.target.files) {
									handleUploadPDF(
										event.target.files[0],
										title,
										'',
										setDocumentUpload,
										(titleToast, subtitleToast, typeToast) =>
											showToast(
												titleToast,
												subtitleToast,
												typeToast as 'success' | 'error' | 'warning'
											)
									)
								}
							}}
							accept="application/pdf"
						/>
						<div className=" py-4">
							{loading ? (
								<div className="flex h-full w-full justify-center items-center">
									<CircularProgress disableShrink />
								</div>
							) : (
								stateChange()
							)}
							<Button
								type="submit"
								label="Add Document"
								color="Primary"
								className="w-full mt-4"
								disabled={documentUpload === undefined}
							/>
						</div>

						{fields.map((component, index) => {
							return (
								<Controller
									key={`documents-${component.id}`}
									control={control}
									name={`documents.${index}`}
									render={({ field }) => {
										return (
											<div className="mt-4 flex flex-col gap-4 border-b-[0.5px] border-gray-3 pb-4">
												<BoxDocuments
													size={field.value.size}
													title={field.value.title}
													onClick={() => remove(index)}
													labelButton="Delete PDF"
												/>

												<BoxSwitch
													title="Activate Document Privacy"
													enabled={field.value.is_private}
													icon={Icons.privacy}
													sizeIcon="w-[20px] h-[22px]"
													helper="Once privacy is enabled, this document can’t be shared or downloaded within the app."
													setEnabled={() => {
														field.onChange({
															...field.value,
															is_private: !field.value.is_private
														})

														setValueForm(
															`${name}.${pos}.component_value.documents.${index}` as Path<{
																tabs: TabMyOrganizationItem[]
															}>,
															{
																...field.value,
																is_private: !field.value.is_private
															}
														)
													}}
												/>
											</div>
										)
									}}
								/>
							)
						})}
					</div>
				</form>
			</div>
		</div>
	)
}
